import { onMounted, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import customerService, { type Customer } from "@/services/customer";
import supplierService, {
  type Supplier,
  type PaginatedResponse,
} from "@/services/supplier";
import userService, { type User } from "@/services/user";
import branchService, { type Branch } from "@/services/branch";
import billerService, { type Biller } from "@/services/biller";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import rolePermissionService, { type RolePermission } from "@/services/role_permission";
import { getUser, type StoredUser } from "@/utils/auth";

export function useCustomerList() {
  const { t } = useI18n();
  const loading = ref(false);
  const detailsLoading = ref(false);
  const searchQuery = ref("");
  const customers = ref<Customer[]>([]);
  const selectedCustomer = ref<Customer | null>(null);
  const editingCustomer = ref<Customer | null>(null);

  const filters = reactive({
    status: "",
    country: "",
  });

  const activeFilters = ref({
    status: "",
    country: "",
  });

  const fetchCustomers = async () => {
    loading.value = true;
    try {
      customers.value = await customerService.getCustomers({
        q: searchQuery.value || undefined,
        status: activeFilters.value.status || undefined,
        country: activeFilters.value.country || undefined,
      });
    } catch (error) {
      console.error("Error fetching customers:", error);
      await Swal.fire({
        icon: "error",
        title: t("customer_page.failed_to_load"),
        text: t("customer_page.unable_to_load_customers"),
      });
    } finally {
      loading.value = false;
    }
  };

  const applyFilters = () => {
    activeFilters.value = { ...filters };
    fetchCustomers();
  };

  const resetFilters = () => {
    filters.status = "";
    filters.country = "";
    applyFilters();
  };

  const openCreateCustomer = () => {
    editingCustomer.value = null;
  };

  const openCustomerDetails = async (customer: Customer) => {
    if (!customer.id) {
      selectedCustomer.value = customer;
      return;
    }

    detailsLoading.value = true;
    try {
      selectedCustomer.value = await customerService.getCustomer(customer.id);
    } catch (error) {
      console.error("Error fetching customer details:", error);
      selectedCustomer.value = customer;
      await Swal.fire({
        icon: "error",
        title: t("customer_page.detail_failed"),
        text: t("customer_page.unable_to_load_customer_details"),
      });
    } finally {
      detailsLoading.value = false;
    }
  };

  const openEditCustomer = async (customer: Customer) => {
    if (!customer.id) {
      editingCustomer.value = customer;
      return;
    }

    try {
      editingCustomer.value = await customerService.getCustomer(customer.id);
    } catch (error) {
      console.error("Error fetching customer for edit:", error);
      editingCustomer.value = customer;
      await Swal.fire({
        icon: "error",
        title: t("customer_page.edit_failed"),
        text: t("customer_page.unable_to_load_customer_for_edit"),
      });
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    if (!customer.id) return;

    const result = await Swal.fire({
      title: t("customer_page.delete_customer_title"),
      text: t("customer_page.delete_customer_text", { name: customer.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("customer_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await customerService.deleteCustomer(customer.id);
      await fetchCustomers();
      await Swal.fire({
        icon: "success",
        title: t("customer_page.deleted"),
        text: t("customer_page.customer_deleted_successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting customer:", error);
      await Swal.fire({
        icon: "error",
        title: t("customer_page.delete_failed"),
        text: t("customer_page.unable_to_delete_customer"),
      });
    }
  };

  const handleCustomerSaved = async () => {
    editingCustomer.value = null;
    selectedCustomer.value = null;
    await fetchCustomers();
  };

  watch(searchQuery, fetchCustomers);
  onMounted(fetchCustomers);

  return {
    loading,
    detailsLoading,
    searchQuery,
    customers,
    selectedCustomer,
    editingCustomer,
    filters,
    applyFilters,
    resetFilters,
    openCreateCustomer,
    openCustomerDetails,
    openEditCustomer,
    handleDeleteCustomer,
    handleCustomerSaved,
  };
}

export function useSupplierList() {
  const { t } = useI18n();
  const loading = ref(false);
  const detailsLoading = ref(false);
  const searchQuery = ref("");
  const suppliers = ref<Supplier[]>([]);
  const selectedSupplier = ref<Supplier | null>(null);
  const editingSupplier = ref<Supplier | null>(null);

  const filters = reactive({
    status: "",
  });

  const activeFilters = ref({
    status: "",
  });

  const pagination = reactive({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });

  const fetchSuppliers = async () => {
    loading.value = true;
    try {
      const response = (await supplierService.getSuppliers({
        search: searchQuery.value || undefined,
        status: activeFilters.value.status || undefined,
        page: pagination.current_page,
        per_page: pagination.per_page,
      })) as PaginatedResponse<Supplier>;

      suppliers.value = Array.isArray(response?.data) ? response.data : [];
      pagination.current_page = Number(response?.current_page || 1);
      pagination.per_page = Number(response?.per_page || pagination.per_page);
      pagination.total = Number(response?.total || suppliers.value.length);
      pagination.last_page = Number(response?.last_page || 1);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      await Swal.fire({
        icon: "error",
        title: t("supplier_page.failed_to_load"),
        text: t("supplier_page.unable_to_load_suppliers"),
      });
    } finally {
      loading.value = false;
    }
  };

  const applyFilters = () => {
    activeFilters.value = { ...filters };
    pagination.current_page = 1;
    fetchSuppliers();
  };

  const resetFilters = () => {
    filters.status = "";
    applyFilters();
  };

  const openCreateSupplier = () => {
    editingSupplier.value = null;
  };

  const openSupplierDetails = async (supplier: Supplier) => {
    if (!supplier.id) {
      selectedSupplier.value = supplier;
      return;
    }

    detailsLoading.value = true;
    try {
      selectedSupplier.value = await supplierService.getSupplier(supplier.id);
    } catch (error) {
      console.error("Error fetching supplier details:", error);
      selectedSupplier.value = supplier;
      await Swal.fire({
        icon: "error",
        title: t("supplier_page.detail_failed"),
        text: t("supplier_page.unable_to_load_supplier_details"),
      });
    } finally {
      detailsLoading.value = false;
    }
  };

  const openEditSupplier = async (supplier: Supplier) => {
    if (!supplier.id) {
      editingSupplier.value = supplier;
      return;
    }

    try {
      editingSupplier.value = await supplierService.getSupplier(supplier.id);
    } catch (error) {
      console.error("Error fetching supplier for edit:", error);
      editingSupplier.value = supplier;
      await Swal.fire({
        icon: "error",
        title: t("supplier_page.edit_failed"),
        text: t("supplier_page.unable_to_load_supplier_for_edit"),
      });
    }
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    if (!supplier.id) return;

    const result = await Swal.fire({
      title: t("supplier_page.delete_supplier_title"),
      text: t("supplier_page.delete_supplier_text", { name: supplier.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("supplier_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await supplierService.deleteSupplier(supplier.id);
      await fetchSuppliers();
      await Swal.fire({
        icon: "success",
        title: t("supplier_page.deleted"),
        text: t("supplier_page.supplier_deleted_successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting supplier:", error);
      await Swal.fire({
        icon: "error",
        title: t("supplier_page.delete_failed"),
        text: t("supplier_page.unable_to_delete_supplier"),
      });
    }
  };

  const handleSupplierSaved = async () => {
    editingSupplier.value = null;
    selectedSupplier.value = null;
    await fetchSuppliers();
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    pagination.current_page = page;
    if (pageSize) pagination.per_page = pageSize;
    fetchSuppliers();
  };

  watch(searchQuery, () => {
    pagination.current_page = 1;
    fetchSuppliers();
  });

  onMounted(fetchSuppliers);

  return {
    loading,
    detailsLoading,
    searchQuery,
    suppliers,
    selectedSupplier,
    editingSupplier,
    filters,
    pagination,
    applyFilters,
    resetFilters,
    openCreateSupplier,
    openSupplierDetails,
    openEditSupplier,
    handleDeleteSupplier,
    handleSupplierSaved,
    handlePageChange,
  };
}

export function useUserList() {
  const { t } = useI18n();
  const loading = ref(false);
  const detailsLoading = ref(false);
  const searchQuery = ref("");
  const users = ref<User[]>([]);
  const roles = ref<RolePermission[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const branches = ref<Branch[]>([]);
  const billers = ref<Biller[]>([]);
  const selectedUser = ref<User | null>(null);
  const editingUser = ref<User | null>(null);
  const currentUser =
    getUser() as (StoredUser & { role_permission?: { role_name?: string } }) | null;

  const filters = reactive({
    status: "",
    role: "",
  });

  const activeFilters = ref({
    status: "",
    role: "",
  });

  const fetchUsers = async () => {
    loading.value = true;
    try {
      users.value = await userService.getUsers({
        search: searchQuery.value || undefined,
        status: activeFilters.value.status || undefined,
        role: activeFilters.value.role || undefined,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      await Swal.fire({
        icon: "error",
        title: t("user_page.failed_to_load"),
        text: t("user_page.unable_to_load_users"),
      });
    } finally {
      loading.value = false;
    }
  };

  const fetchFormOptions = async () => {
    try {
      const [rolesResponse, warehouseData, branchData, billerData] = await Promise.all([
        rolePermissionService.getRoles(),
        warehouseService.getWarehouses(),
        branchService.getBranches(),
        billerService.getBillers(),
      ]);

      const allRoles = Array.isArray(rolesResponse)
        ? rolesResponse
        : rolesResponse?.data || [];

      const filteredRoles =
        currentUser?.role_permission?.role_name === "Admin"
          ? allRoles.filter((role: RolePermission) => role.role_name !== "Super Admin")
          : allRoles;

      roles.value = filteredRoles.length ? filteredRoles : allRoles;

      warehouses.value = Array.isArray(warehouseData) ? warehouseData : [];
      branches.value = Array.isArray(branchData) ? branchData : [];
      billers.value = Array.isArray(billerData) ? billerData : [];
    } catch (error) {
      console.error("Error fetching user form options:", error);
      await Swal.fire({
        icon: "error",
        title: t("user_page.failed_to_load"),
        text: t("user_page.unable_to_load_user_form_options"),
      });
    }
  };

  const applyFilters = () => {
    activeFilters.value = { ...filters };
    fetchUsers();
  };

  const resetFilters = () => {
    filters.status = "";
    filters.role = "";
    applyFilters();
  };

  const openCreateUser = () => {
    editingUser.value = null;
    if (!roles.value.length || !warehouses.value.length || !branches.value.length || !billers.value.length) {
      fetchFormOptions();
    }
  };

  const openUserDetails = async (user: User) => {
    if (!user.id) {
      selectedUser.value = user;
      return;
    }

    detailsLoading.value = true;
    try {
      selectedUser.value = await userService.getUser(user.id);
    } catch (error) {
      console.error("Error fetching user details:", error);
      selectedUser.value = user;
      await Swal.fire({
        icon: "error",
        title: t("user_page.detail_failed"),
        text: t("user_page.unable_to_load_user_details"),
      });
    } finally {
      detailsLoading.value = false;
    }
  };

  const openEditUser = async (user: User) => {
    if (!roles.value.length || !warehouses.value.length || !branches.value.length || !billers.value.length) {
      await fetchFormOptions();
    }

    if (!user.id) {
      editingUser.value = user;
      return;
    }

    try {
      editingUser.value = await userService.getUser(user.id);
    } catch (error) {
      console.error("Error fetching user for edit:", error);
      editingUser.value = user;
      await Swal.fire({
        icon: "error",
        title: t("user_page.edit_failed"),
        text: t("user_page.unable_to_load_user_for_edit"),
      });
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!user.id) return;

    const result = await Swal.fire({
      title: t("user_page.delete_user_title"),
      text: t("user_page.delete_user_text", { username: user.username }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("user_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await userService.deleteUser(user.id);
      await fetchUsers();
      await Swal.fire({
        icon: "success",
        title: t("user_page.deleted"),
        text: t("user_page.user_deleted_successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting user:", error);
      await Swal.fire({
        icon: "error",
        title: t("user_page.delete_failed"),
        text: t("user_page.unable_to_delete_user"),
      });
    }
  };

  const handleUserSaved = async () => {
    editingUser.value = null;
    selectedUser.value = null;
    await fetchUsers();
  };

  watch(searchQuery, fetchUsers);
  onMounted(async () => {
    await fetchFormOptions();
    await fetchUsers();
  });

  return {
    loading,
    detailsLoading,
    searchQuery,
    users,
    roles,
    warehouses,
    branches,
    billers,
    selectedUser,
    editingUser,
    filters,
    applyFilters,
    resetFilters,
    openCreateUser,
    openUserDetails,
    openEditUser,
    handleDeleteUser,
    handleUserSaved,
    fetchFormOptions,
  };
}
